(ns frontend.view.grid.tasks-grid.events
  (:require
   [frontend.view.grid.tasks-grid.db :as db]
   [frontend.view.grid.tasks-grid.db :as tasks-db]
   [frontend.view.grid.tasks-grid.columns :as columns]
   [frontend.analytics :as analytics]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [common.localization :as loc]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [index-by fn-> fn->> deep-merge]]
   [frontend.view.modals.curtains.filter-curtain.view :as filter-curtain]
   [frontend.state.current-session :as session]
   [frontend.actions.task :as task]
   [frontend.view.curtains.webentry-resolution.events :as webentry-resolution-events]
   [frontend.logging :as logging]
   [frontend.utils :as utils]
   [frontend.routing :as routing]
   [frontend.conn :as conn]
   [frontend.local-storage :as ls]
   [promesa.core :as p]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.tenant-customizations :as tenant-customizations]
   [reagent.core :as r]
   [re-frame.core :as rf]
   [frontend.utils :as fu]
   [frontend.ux-overhaul.view-configuration.common :as view-config-common]
   [common.util :as cu]
   [frontend.db :as frontend-db]
   [frontend.actions.entity :as entity-actions]
   [frontend.actions.account :as account-actions]
   [frontend.view.error-utils :as error-utils]))

(defn get-view
  []
  (or (:tasks-view @db/tasks)
      :default))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :tasks-view-settings
    ;; separate ls-path for different task-types:
    (get-view)]
   rest))

(defn coerce-value
  [type value]
  (case type
    :currency (utils/bigdec->float value)
    :currency--sym (utils/bigdec->float value)
    :integer value
    :percentage value
    :entity (:db/id value)
    :owner-account value
    :switch (boolean value)
    (str value)))

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/tasks
   :modal-state db/modal-state
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/task-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :column-keys columns/column-keys
   :default-columns (columns/default-columns)
   :mandatory? columns/mandatory?})

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/tasks)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/tasks)]
    (r/dispose! tracker))
  (when-let [tracker (:view-configuration-tracker @db/tasks)]
    (r/dispose! tracker))

  (swap! db/tasks dissoc
    :loaded
    :entities
    :entity-types
    :view-settings
    :grid-entities
    :sorted-grid-entities
    :search
    :selected))

;;note: ordered-columns is read from local-storage by default, and linked
;; directly to static named-views (to allow saving column order):
(defn get-ordered-columns
  []
  (let [selected-view (get-view)
        ordered (or (ls/get-in (ls-path :ordered-columns))
                    (columns/default-columns))]
    ordered))

;; my pending, all pending, my completed, all completed, my all, all
(defn named-views
  []
  (let [CAPMAN? (tenant-customizations/capman?)]
    (concat
     [{:named-view/settings {:selected-columns (set (columns/default-columns))
                             :ordered-columns  (get-ordered-columns)
                             :filters          {:or/assignee_OR_team-members
                                                {:filter/type          :filter.type/or-combination
                                                 :lower-bound-value    2
                                                 :filter/child-filters
                                                 {:task/assignee       {:operand :operand/any-of
                                                                        :lower-bound-value
                                                                        [(:db/id
                                                                          @session/account)]}
                                                  :common/team-members
                                                  {:operand :operand/any-of
                                                   :lower-bound-value
                                                   [(:db/id @session/account)]}}}
                                                :task/completed? {:operand :operand/false
                                                                  :upper-bound-value :set}}
                             :sort-by          :common/created-on
                             :sort-direction   "DESC"}
       :named-view/name (loc/tr :my-pending)
       :named-view/key :my-pending
       :db/id "named-view-my-pending-tasks"}]
     (when (or (not CAPMAN?)
               (session/is-admin?))
       [{:named-view/settings {:selected-columns (set (columns/default-columns))
                               :ordered-columns  (get-ordered-columns)
                               :filters          {:task/completed? {:operand :operand/false
                                                                    :upper-bound-value :set}}
                               :sort-by          :common/created-on
                               :sort-direction   "DESC"}
         :named-view/name (loc/tr :all-pending)
         :named-view/key :all-pending
         :db/id "named-view-all-pending-tasks"}])

     [{:named-view/settings {:selected-columns (set (columns/default-columns))
                             :ordered-columns  (get-ordered-columns)
                             :filters          {:or/assignee_OR_team-members
                                                {:filter/type          :filter.type/or-combination
                                                 :lower-bound-value    2
                                                 :filter/child-filters
                                                 {:task/assignee       {:operand :operand/any-of
                                                                        :lower-bound-value
                                                                        [(:db/id
                                                                          @session/account)]}
                                                  :common/team-members
                                                  {:operand :operand/any-of
                                                   :lower-bound-value
                                                   [(:db/id @session/account)]}}}
                                                :task/completed? {:operand :operand/true
                                                                  :upper-bound-value :set}}
                             :sort-by          :common/created-on
                             :sort-direction   "DESC"}
       :named-view/name (loc/tr :my-completed)
       :named-view/key :my-completed
       :db/id "named-view-my-completed-tasks"}]
     (when (or (not CAPMAN?)
               (session/is-admin?))
       [{:named-view/settings {:selected-columns (set (columns/default-columns))
                               :ordered-columns  (get-ordered-columns)
                               :filters          {:task/completed? {:operand :operand/true
                                                                    :upper-bound-value :set}}
                               :sort-by          :common/created-on
                               :sort-direction   "DESC"}
         :named-view/name (loc/tr :all-completed)
         :named-view/key :all-completed
         :db/id "named-view-all-completed-tasks"}
        {:named-view/settings {:selected-columns (set (columns/default-columns))
                               :ordered-columns  (get-ordered-columns)
                               :filters          {:or/assignee_OR_team-members
                                                  {:filter/type          :filter.type/or-combination
                                                   :lower-bound-value    2
                                                   :filter/child-filters
                                                   {:task/assignee       {:operand :operand/any-of
                                                                          :lower-bound-value
                                                                          [(:db/id
                                                                            @session/account)]}
                                                    :common/team-members
                                                    {:operand :operand/any-of
                                                     :lower-bound-value
                                                     [(:db/id @session/account)]}}}}
                               :sort-by          :common/created-on
                               :sort-direction   "DESC"}
         :named-view/name (loc/tr :my-all)
         :named-view/key :my-all
         :db/id "named-view-my-all-tasks"}
        {:named-view/settings {:selected-columns (set (columns/default-columns))
                               :ordered-columns  (get-ordered-columns)
                               :sort-by          :common/created-on
                               :sort-direction   "DESC"}
         :named-view/name (loc/tr :all-accounts)
         :named-view/key :all
         :db/id "named-view-all-tasks"}])
     #_(->> @session/all-named-views
            (cu/only-entity :named-view/view :tasks)
            (sort-by :common/created-on)))))

(defn setup-column-state!
  [{:keys [named-view/settings named-view/key]}]
  (reset! db/view-settings settings)
  (swap! db/tasks assoc :named-view/key key)
  (when (nil? @db/selected-columns)
    (reset! db/selected-columns (set (columns/default-columns)))
    (reset! db/ordered-columns (get-ordered-columns))
    ;;no saving to local-storage for now
    #_(ls/update-in (ls-path) assoc :selected-columns (set (columns/default-columns))))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn enrich-email-tasks
  [tasks]
  (->> tasks
       (mapv (fn [task]
               (assoc task :task/message (loc/tr :resolve-email))))))

(defn enrich-webentry-tasks
  [tasks]
  (->> tasks
       (mapv (fn [task]
               (let [task-data (:task/data task)
                     asset-type (:webentry/asset-type task-data)
                     asset-name (-> task-data
                                    (get asset-type)
                                    :asset/name)
                     contact (->> task-data
                                  :entity.type/person
                                  first)
                     {:person/keys [first-name last-name]} contact]
                 (cond-> (assoc task :task/message (loc/tr :resolve-deal-entry-task-title))
                   asset-name (assoc :webentry-task/asset-name asset-name)
                   first-name (assoc :webentry-task/first-name first-name)
                   last-name (assoc :webentry-task/last-name last-name)))))))

(defn task-updates
  []
  (if (session/has-feature-access? :feature/tasks-kanban)
    (let [; Todo quick fix to show all task types with tasks kanban view
          enriched-tasks (some->> @session/tasks
                                  (map (fn [task]
                                         (case (:task/type task)
                                           :task.type/simple task
                                           :task.type/email (->> [task]
                                                                 (enrich-email-tasks)
                                                                 first)
                                           :task.type/webentry (->> [task]
                                                                    (enrich-webentry-tasks)
                                                                    first)
                                           task))))]
      (swap! db/tasks assoc :grid-entities enriched-tasks))

    (let [task-type (:tasks-view @db/tasks)
          tasks (some->> @session/tasks
                         (cu/filter-by :task/type task-type))
          enriched-tasks (case task-type
                           :task.type/webentry (enrich-webentry-tasks tasks)
                           :task.type/email (enrich-email-tasks tasks)
                           tasks)]
      (swap! db/tasks assoc :grid-entities enriched-tasks))))

(defn trigger-recalculation
  []
  (let [entities-trigger @db/grid-entities]
    (when (or entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/tasks assoc :loaded true)
                    (deref db/tasks))))))

(defn setup
  [closed-atom? predefined-filter]
  (when-not @closed-atom?
    (let [tasks-kanban-feature? (session/has-feature-access? :feature/tasks-kanban)
          named-view (->> (named-views)
                          (cu/filter-by :named-view/key predefined-filter)
                          first)
          named-view (or named-view
                         (if tasks-kanban-feature?
                           (last (named-views))
                           (first (named-views))))]
      (swap! db/tasks assoc
        :tracker-1 (r/track! task-updates)
        :tracker-2 (r/track! trigger-recalculation))
      (setup-column-state! named-view))))

(defn set-tasks-view
  [task-type]
  (swap! db/tasks assoc :selected {})
  (swap! db/tasks assoc :tasks-view task-type)
  (reset! db/selected-columns (set (columns/default-columns)))
  (reset! db/ordered-columns (get-ordered-columns))
  (grid/recalculate (setup-args)))

(defn toggle-named-views!
  []
  (swap! db/tasks update :named-views-minimized? not))

(defn load-view!
  [named-view] ;;named-view entity
  ;;no saving to local-storage for now
  #_(ls/set-in (ls-path) (:named-view/settings named-view))
  (setup-column-state! named-view))

(defn mass-delete!
  []
  (-> (conn/sendp! :task/mass-action
                   {:action   :delete-tasks
                    :task-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [args]
                (swap! db/tasks assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected tasks" e)))))


(defn delete-selected-clicked
  [_]
  (modals/open-modal!
   confirmation/modal-key
   {:body          [:div
                    [:p (loc/tr :confirm-delete-selected)]]
    :confirm-text  (loc/tr :confirm-delete-selected-button)
    :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
    :on-confirm    #(mass-delete!)}))

(defn handle-webentry
  [task]
  (reset! db/webentry-task task)
  (cond
    (->> task
         :task/data
         :version
         :v3)
    (routing/set-view! [:task-resolve/webentry :id (:db/id task)])
    (->> task
         :task/data
         :version
         :vtesi-2)
    (webentry-resolution-events/open-curtain! task true)
    :else
    (webentry-resolution-events/open-curtain! task false)))

(defn discard-webentry!
  [task processing-ended]
  (entity-actions/*->disable! (:db/id task)
                              processing-ended
                              error-utils/show-error!))

(defn resolve-webentry!
  [task discard? existing-company-id processing-ended on-error]
  (let [msg {:task-id (:db/id task)}
        msg (cond-> msg
              (and (not discard?) existing-company-id)
              (assoc :company-id existing-company-id))]
    (if discard?
      (discard-webentry! task processing-ended)
      (-> (conn/sendp! :webentry/resolve msg)
          (p/then
           (fn [{:keys [body] :as ctx}]
             (let [company-id (:company-id body)
                   oppo-id (:oppo-id body)]
               (if (and (not (number? company-id))
                        (not (number? oppo-id)))
                 (logging/warn "Could not resolve target : " body)
                 (js/setTimeout (fn [_]
                                  (processing-ended)
                                  (routing/open-entity (or company-id oppo-id)))
                                1000)))))
          (p/catch on-error)))
    (analytics/event (if discard?
                       "Webform-task-discarded"
                       "Webform-task-resolved"))))

(defn reactivate-task!
  [task]
  (task/*reactivate-task-> {:db/id (:db/id task)}
                           identity
                           (fn [err]
                             (logging/error "failed to reactivate task" err))))

(defn complete-task!
  [task]
  (task/*complete-task-> {:db/id (:db/id task)}
                         identity
                         (fn [err]
                           (logging/error "failed to complete task" err))))

(defn handle-email
  [task]
  (routing/set-view! [:task-resolve/email :id (:db/id task)]))


; Re-Frame and New Kanban

(defn tasks-view-config-tracker
  []
  (let [session-view-config @session/view-config
        entity-type :entity.type/task
        tasks-view-config (get-in session-view-config [:view-configuration entity-type])]
    ; Since session view config also contains other legacy configurations,
    ; we only want to update the new tasks view config if it exists
    ; Todo - This might be anti pattern?
    (when tasks-view-config
      (rf/dispatch [:set-view-configuration entity-type tasks-view-config]))))

(rf/reg-event-db
 :set-view-configuration
 (fn [db [_ entity-type view-configuration]]
   (assoc-in db [:view-configuration entity-type] view-configuration)))

(rf/reg-event-fx
 ::start-tasks
 (fn [{:keys [db]} _]
   (let [tasks (->> @frontend-db/entity-types
                    :entity.type/task
                    (remove :common/disabled?)
                    (reduce (fn [acc item] (assoc acc (:db/id item) item)) {})
                    (into {}))

         entity-type :entity.type/task
         config-path [:view-configuration entity-type]

         saved-view-config (some-> @session/view-config
                                   (get-in config-path))

         view-config (or saved-view-config
                         (view-config-common/get-default-view-config entity-type))

         entities (merge (:entities db)
                         tasks)

         new-db (-> db
                    (merge {:entities entities})
                    (assoc-in config-path view-config))]
     {:db new-db})))

(rf/reg-event-db
 ::set-view-configuration-tracker
 (fn [db [_]]
   ; Todo - this might anti pattern
   ; Track the session state for now to get the new view config on re-frame synced
   (swap! db/tasks assoc :view-configuration-tracker (r/track! tasks-view-config-tracker))

   db))

(rf/reg-event-fx
 ::drag-end
 (fn [{:keys [db]}
      [_
       {:keys [item-id source target target-id
               card-view-configuration]}]]
   (let [group-by-key (-> card-view-configuration
                          :view-settings
                          :grouping
                          :group-by)

         target-task (get-in db [:entities item-id])

         old-value (get target-task group-by-key)

         new-value target-id

         updated-task (assoc target-task group-by-key new-value)]
     {:db (assoc-in db [:entities item-id] updated-task)
      :entity-actions/field-update! {:entity-id item-id
                                     :attribute group-by-key
                                     :new-value new-value
                                     :old-value old-value}})))

(rf/reg-event-db
 ::update-entity-attribute
 (fn [db [_ entity-id attribute value]]
   (assoc-in db [:entities entity-id attribute] value)))

(rf/reg-fx
 :entity-actions/field-update!
 (fn [event]
   (let [{:keys [entity-id attribute new-value old-value]} event]
     (entity-actions/*->field-update!
      entity-id
      attribute
      new-value
      (fn [] (js/console.log "update success"))
      (fn []
        (js/console.log "update failure")
        (rf/dispatch [::update-entity-attribute entity-id attribute old-value])
        (js/setTimeout #(.alert js/window "Entity attribute update failed for some reason!") 100))))))

(rf/reg-event-fx
 ::update-filters
 (fn [{:keys [db]} [_ new-filters]]
   (let [entity-type :entity.type/task
         view-configuration (get-in db [:view-configuration entity-type])
         updated-view-configuration (assoc view-configuration :filters new-filters)]
     {:db (assoc-in db
           [:view-configuration entity-type]
           updated-view-configuration)

      :view-configuration-actions/save!
      {:entity-type        :entity.type/task
       :view-configuration updated-view-configuration}})))

(rf/reg-fx
 :view-configuration-actions/save!
 (fn [event]
   (let [{:keys [entity-type view-configuration]} event
         data {:path      [:view-configuration entity-type]
               :new-value view-configuration}]
     (account-actions/*->update-view-config
      data
      (fn [])
      (fn [] (js/console.log "Failure updating view config!"))))))
